import { defineMessages } from 'react-intl-next';

import { useSessionData, ConfluenceEdition } from '@confluence/session-data';

import { useGetUserCount } from './useGetUserCount';

export const closeModalCTA = defineMessages({
	text: {
		id: 'trial-components.auto-upgrade-awareness.modal.closeModalCTA',
		defaultMessage: 'Maybe later',
		description: 'Free modal CTA',
	},
});

export const freeModalCTA = defineMessages({
	text: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeModalCTA',
		defaultMessage: 'Start free trial',
		description: 'Free modal CTA',
	},
});

export const standardModalCTA = defineMessages({
	text: {
		id: 'trial-components.upgrade-awareness.modal.standardModalCTA',
		defaultMessage: 'Add payment details',
		description: 'Standard modal CTA',
	},
});

export const freeStorageUsage = defineMessages({
	header: {
		id: 'trial-components.vvauto-upgrade-awareness.modal.freeStorageUsageHeader',
		defaultMessage: '{dataUsageinGB} of 2 GB used',
		description: 'Free storage usage header section',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeStorageUsageSubHeader',
		defaultMessage: 'Upgrade for 250 GB of storage',
		description: 'Free storage usage subheader section',
	},
});

export const standardTrialStorageUsage = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardStorageUsageHeader',
		defaultMessage: '{dataUsageinGB} of 250 GB used',
		description: 'Standard storage usage header section',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardStorageUsageSubHeader',
		defaultMessage: 'You’re {dataUsageOverFreeLimitInGB} GB over the Free limit',
		description: 'Standard storage usage subheader section',
	},
});

export const freeUserAvatar1to2Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeUserAvatarHeader1to2Users',
		defaultMessage: '{userCount} of 10 invited',
		description: 'Free user avatar header section for 1 to 2 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeUserAvatarSubHeader1to2Users',
		defaultMessage: 'Upgrade for unlimited users',
		description: 'Free user avatar subheader section for 1 to 2 users',
	},
});

export const freeUserAvatar3to10Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeUserAvatarHeader3to10Users',
		defaultMessage: '{userCount} of 10 invited',
		description: 'Free user avatar header section for 3 to 10 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeUserAvatarSubHeader3to10Users',
		defaultMessage: 'Inviting more than 10 users will start a free, 14-day trial of Standard',
		description: 'Free user avatar subheader section for 3 to 10 users',
	},
});

export const standardTrialUserAvatar10orLessUsers = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardUserAvatarHeader10orLessUsers',
		defaultMessage: 'Take advantage of unlimited users',
		description: 'Standard user avatar header section for 10 or less users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardUserAvatarSubHeader10orLessUsers',
		defaultMessage: 'Invite your teammates and stakeholders to collaborate',
		description: 'Standard user avatar subheader section for 10 or less users',
	},
});

export const standardTrialUserAvatar11orMoreUsers = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardUserAvatarHeader11PlusUsers',
		defaultMessage: 'You’re taking advantage of unlimited users',
		description: 'Standard user avatar header section for 11+ users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardUserAvatarSubHeader11PlusUsers',
		defaultMessage:
			'Your team is {userLimitOverFreeLimit} {userLimitOverFreeLimit, plural, one {user} other {users}} over the Free limit',
		description: 'Standard user avatar subheader section for 11+ users',
	},
});

export const modalCarousel = defineMessages({
	unlimitedUserLimit: {
		id: 'trial-components.auto-upgrade-awareness.modal.carouselUnlimitedUserLimit',
		defaultMessage: 'Unlimited user limit',
		description: 'Unlimited user limit carousel slide',
	},
	pageInsights: {
		id: 'trial-components.auto-upgrade-awareness.modal.carouselPageInsights',
		defaultMessage: 'Page insights',
		description: 'Page insights carousel slide',
	},
	pageRestrictions: {
		id: 'trial-components.auto-upgrade-awareness.modal.carouselPageRestrictions',
		defaultMessage: 'Page restrictions',
		description: 'Page restrictions carousel slide',
	},
	guestAccess: {
		id: 'trial-components.auto-upgrade-awareness.modal.carouselGuestAccess',
		defaultMessage: 'Guest access',
		description: 'Guest access carousel slide',
	},
});

export const free1to2Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeHeader1to2Users',
		defaultMessage: 'Get everyone in one tool',
		description: 'Free modal header 1-2 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeSubHeader1to2Users',
		defaultMessage:
			'Start a free, 14-day Standard trial to add more users and stay in sync with your team and stakeholders.',
		description: 'Free modal subheader 1-2 users',
	},
});

export const free3to9Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeHeader3to9Users',
		defaultMessage: 'Upgrade to add your team',
		description: 'Free modal header 3-9 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeSubHeader3to9Users',
		defaultMessage:
			'Start a free, 14-day Standard trial to add more users, also get free guest access and page access controls.',
		description: 'Free modal subheader 3-9 users',
	},
});

export const free10Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeHeader10Users',
		defaultMessage: 'You’re out of free users',
		description: 'Free modal header 10 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.freeSubHeader10Users',
		defaultMessage:
			'Start a free, 14-day trial of Standard to get unlimited users. Plus, get access to advanced features.',
		description: 'Free modal subheader 10 users',
	},
});

export const standardTrial10Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardHeader10Users',
		defaultMessage: 'Take action on your trial',
		description: 'Standard modal header 10 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardSubHeader10Users',
		defaultMessage:
			'Add payment details to keep Standard features after your trial ends. Your trial ends in {daysRemaining} {daysRemaining, plural, one {day} other {days}}.',
		description: 'Standard modal subheader 10 users',
	},
});

export const standardTrial1to9Users = defineMessages({
	header: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardHeader1to9Users',
		defaultMessage: 'Take action on your trial',
		description: 'Standard modal header 1 to 9 users',
	},
	subHeader: {
		id: 'trial-components.auto-upgrade-awareness.modal.standardSubHeader1to9Users',
		defaultMessage:
			'Add payment details to keep Standard features after your trial ends. Your trial ends in {daysRemaining} {daysRemaining, plural, one {day} other {days}}.',
		description: 'Standard modal subheader 1 to 9 users',
	},
});

export const mapModalTranslations = (userCount: number, edition: ConfluenceEdition | null) =>
	edition === ConfluenceEdition.FREE
		? userCount === 10
			? free10Users
			: userCount >= 3
				? free3to9Users
				: free1to2Users
		: userCount >= 10
			? standardTrial10Users
			: standardTrial1to9Users;

export const mapUserAvatarTranslations = (userCount: number, edition: ConfluenceEdition | null) =>
	edition === ConfluenceEdition.FREE
		? userCount < 3
			? freeUserAvatar1to2Users
			: freeUserAvatar3to10Users
		: userCount <= 10
			? standardTrialUserAvatar10orLessUsers
			: standardTrialUserAvatar11orMoreUsers;

export const useAUATranslations = () => {
	const { userCount } = useGetUserCount();
	const { edition } = useSessionData();
	const modalTranslations = mapModalTranslations(userCount, edition);
	const modalUserAvatar = mapUserAvatarTranslations(userCount, edition);
	return {
		modalTranslations,
		modalPrimaryCTA: edition === ConfluenceEdition.FREE ? freeModalCTA : standardModalCTA,
		modalUserAvatar,
		modalStorageUsage:
			edition === ConfluenceEdition.FREE ? freeStorageUsage : standardTrialStorageUsage,
		modalCloseCTA: closeModalCTA,
		modalCarousel,
	};
};
